// ============================================================
// Wiederverwendbarer „🔔 Erinnerung setzen"-Knopf + Dialog.
// Eingesetzt an Terminen (Kalender), Fristen (Begehungs-Mängel) und
// ToDos (Akte). Legt eine Erinnerung an, die der Telegram-Bot am
// gewählten Tag ausliefert.
// ============================================================

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { createErinnerung, type ErinnerungTyp } from '../services/erinnerungService';

interface Props {
  titel: string;                          // Vorbelegung (administrativ, keine Personennamen)
  typ: ErinnerungTyp;
  basisDatum?: string | undefined;        // ISO-Datum des Termins/der Frist (für „x Tage vorher")
  mandantId?: string | undefined;
  quelle?: string | undefined;            // Ursprung, z.B. "begehung:<id>", "akte:<id>", "gcal:<eventId>"
  label?: string | undefined;             // Button-Text (Standard: nur Glocke)
  className?: string | undefined;
}

function heute(): string {
  return new Date().toISOString().slice(0, 10);
}
function addDays(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
function fmt(iso: string): string {
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}.${m}.${y}` : iso;
}

export default function ErinnerungButton({
  titel, typ, basisDatum, mandantId, quelle, label, className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [datum, setDatum] = useState('');
  const [text, setText] = useState('');
  const [notiz, setNotiz] = useState('');
  const [gespeichert, setGespeichert] = useState(false);

  function oeffnen() {
    setText(titel);
    setNotiz('');
    // Vorschlag: Basisdatum (falls in der Zukunft) sonst morgen.
    const vorschlag = basisDatum && basisDatum >= heute() ? basisDatum : addDays(heute(), 1);
    setDatum(vorschlag);
    setGespeichert(false);
    setOpen(true);
  }

  async function speichern() {
    if (!datum) return;
    await createErinnerung({
      titel: text, remind_datum: datum, typ, mandant_id: mandantId, quelle,
      notiz: notiz || undefined,
    });
    setGespeichert(true);
    setTimeout(() => setOpen(false), 900);
  }

  // Schnellauswahl-Optionen: relativ zu Basisdatum (Termin/Frist) oder zu heute.
  const presets = basisDatum
    ? [
        { label: 'am Tag selbst', val: basisDatum },
        { label: '1 Tag vorher', val: addDays(basisDatum, -1) },
        { label: '3 Tage vorher', val: addDays(basisDatum, -3) },
        { label: '1 Woche vorher', val: addDays(basisDatum, -7) },
      ]
    : [
        { label: 'heute', val: heute() },
        { label: 'morgen', val: addDays(heute(), 1) },
        { label: 'in 3 Tagen', val: addDays(heute(), 3) },
        { label: 'nächste Woche', val: addDays(heute(), 7) },
      ];

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); oeffnen(); }}
        title="Erinnerung setzen (Telegram)"
        className={className ?? 'text-xs text-gray-400 hover:text-amber-600'}
      >
        {label ? `🔔 ${label}` : '🔔'}
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full sm:max-w-md bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100">🔔 Erinnerung setzen</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>

            {gespeichert ? (
              <p className="py-6 text-center text-green-600 font-medium">
                ✓ Erinnerung gespeichert für {fmt(datum)}
              </p>
            ) : (
              <>
                <label className="block">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Worum geht's</span>
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-3 py-2 text-sm"
                    placeholder="Titel der Erinnerung"
                  />
                </label>

                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Wann erinnern?</span>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {presets.map((p) => (
                      <button
                        key={p.label}
                        type="button"
                        onClick={() => setDatum(p.val)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                          datum === p.val
                            ? 'bg-amber-500 border-amber-500 text-white'
                            : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-amber-400'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <input
                    type="date"
                    value={datum}
                    min={heute()}
                    onChange={(e) => setDatum(e.target.value)}
                    className="mt-2 w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-3 py-2 text-sm"
                  />
                </div>

                <label className="block">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Notiz (optional)</span>
                  <input
                    value={notiz}
                    onChange={(e) => setNotiz(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-3 py-2 text-sm"
                    placeholder="z.B. Unterlagen mitnehmen"
                  />
                </label>

                <p className="text-[11px] text-gray-400 leading-snug">
                  Kommt am gewählten Tag früh per Telegram (sofern der Bot eingerichtet ist).
                  Es werden nur Firma + Datum + Titel gesendet — keine Personennamen.
                </p>

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => setOpen(false)}
                    className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-600 dark:text-gray-300"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={() => void speichern()}
                    disabled={!datum || !text.trim()}
                    className="flex-1 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 px-3 py-2 text-sm font-medium text-white"
                  >
                    Erinnerung setzen
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
